/* eslint-disable jsx-a11y/anchor-is-valid */
/*
 * @Author: Jiajia Li
 * @Modified By: Jesse Chalker
 * @Date: 2024-08-12 22:07:04
 * @LastEditTime: 2025-04-25 13:43:00
 * @Description:
 */
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import React, { useEffect, useRef, useState, useCallback} from "react";
import { Form, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { getProfileInfo } from "../api/auth";
import { notifySessionExpired } from "../utils/sessionBus";
import filterImage from "../images/filter.png";
import api, { getCache, setCache } from "./api";
import GlobalFilter from "./GlobalFilter";
import Icons from "./Icons";
import NotificationPanel from "./NotificationPanel";
import "./Root.css";

export default function Root() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthRoute = ["/", "/login", "/signup", "/forgot-password"].includes(location.pathname);
  const [showDropdown, setShowDropdown] = useState(false); // Toggle dropdown
  const dropdownRef = useRef(null);

  const searchModalInstance = useRef();
  const [firstName, setFirstName] = useState("");
  const [filterItems, setFilterItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);

  const fetchFilterData = async () => {
    //get details for filter
    let data = getCache("filterItemsV2");
    if (!data) {
      try {
        const response = await api.get("/v2/filters");
        data = await response.data;
        setCache("filterItemsV2", data);
      } catch (error) {
        // Handle error
        console.log(error);
      }
    }
    setFilterItems(data);
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

const fetchProfileInfo = useCallback(async () => {
  if (isAuthRoute) return;
  const token = localStorage.getItem("token");
  if (!token) {
    notifySessionExpired();
    return;
  }
  const response = await getProfileInfo();
  const profileDetails = await response.json();
  setFirstName(profileDetails.first_name);
}, [isAuthRoute]); 

useEffect(() => {
  fetchProfileInfo();
}, [fetchProfileInfo]);

  const toggleDropdown = () => {
    setShowDropdown((prevState) => !prevState);
  };

  const handleNavigation = () => {
    navigate("/user-profile");
    setShowDropdown(false); // Close dropdown
  };

  const handleLogout = () => {
    // Preserve daily check-in data before clearing
    const lastDailyCheckIn = localStorage.getItem("lastDailyCheckIn");

    // Clear the token and other data from localStorage
    localStorage.clear();
    sessionStorage.clear();

    // Restore the daily check-in date so it persists across logins
    if (lastDailyCheckIn) {
      localStorage.setItem("lastDailyCheckIn", lastDailyCheckIn);
    }

    // Redirect to login page
    navigate("/");
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Check if current path is a curu chat page
  const isCuruChatPage = location.pathname.startsWith('/curu-chats');

  return (
    <>
      <header className="sticky-top bg-secondary">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center w-100 py-2">
            <div className="me-auto">
              <Link
                to="/home-page"
                className="navbar-brand p-0 me-0 me-lg-2 text-primary d-flex align-items-center flex-column"
                aria-label="Bootstrap"
              >
                <Icons.Logo />
                <span className="fs-8 mt-1">be your own beauty guru</span>
              </Link>
            </div>
            <div className="flex-grow-1 d-flex justify-content-center">
              <div
                className="bd-search d-flex justify-content-center w-75"
                id="docsearch"
              >
                <Form
                  className="ProductSearch ProductSearch-Button"
                  action="/search"
                  role="search"
                >
                  <svg
                    width="15"
                    height="15"
                    className="ProductSearch-Search-Icon me-2"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      d="M14.386 14.386l4.0877 4.0877-4.0877-4.0877c-2.9418 2.9419-7.7115 2.9419-10.6533 0-2.9419-2.9418-2.9419-7.7115 0-10.6533 2.9418-2.9419 7.7115-2.9419 10.6533 0 2.9419 2.9418 2.9419 7.7115 0 10.6533z"
                      stroke="currentColor"
                      fill="none"
                      fillRule="evenodd"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                  <input
                    type="search"
                    name="q"
                    placeholder="Search products"
                    className="ProductSearch-Input"
                    aria-label="Search products"
                    autoComplete="off"
                  />
                </Form>
              </div>
            </div>
            <div className="ms-auto">
              <ul className="nav justify-content-center fs-7">
                <li>
                  <div className="edit-profile-dropdown" ref={dropdownRef}>
                    <button
                      className="nav-link text-dark px-2"
                      onClick={toggleDropdown}
                    >
                      <Icons.Profile />
                      <span className="d-none d-lg-block">Profile</span>
                    </button>
                    {showDropdown && (
                      <div className="dropdown-content">
                        <p className="greeting">Hello, {firstName}</p>
                        <hr />
                        <button
                          className="dropdown-item py-2"
                          onClick={handleNavigation}
                        >
                          My Profile
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={handleLogout}
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                </li>
                <li>
                  <Link to="/wishlist" className="nav-link text-dark px-2">
                    <Icons.Wishlist />
                    <span className="d-none d-lg-block">Wishlist</span>
                  </Link>
                </li>
                <li>
                  <Link to="/curu-chats" className="nav-link text-dark px-2">
                    <Icons.Chats />
                    <span className="d-none d-lg-block">Curu chats</span>
                  </Link>
                </li>
                <li>
                  <button
                    className="nav-link text-dark px-2"
                    onClick={() => setShowNotifications((prev) => !prev)}
                  >
                    <Icons.Notification />
                    <span className="d-none d-lg-block">Notifications</span>
                  </button>
                </li>
                {!isCuruChatPage && (
                  <li>
                    <button
                      className="nav-link text-dark px-2 filter-button"
                      style={{
                        background: "none",
                        border: "none",
                        padding: "0",
                        position: "relative",
                      }}
                      onClick={handleOpenModal}
                    >
                      <img src={filterImage} alt="filter-icon" />
                      {isFilterActive && (
                        <span className="filter-indicator-dot" />
                      )}
                      <span className="d-none d-lg-block">Filter</span>
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </header>
      {showNotifications && (
        <NotificationPanel onClose={() => setShowNotifications(false)} />
      )}

      <div className="container">
        <Outlet />
      </div>
      <footer className="bd-footer py-4 py-md-5 mt-5 bg-dark fs-7">
        <div className="container">
          <div className="row w-100 text-white">
            <div className="col-6 col-lg">
              <div className="h6">LEGAL STUFF</div>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link to="https://curu.com.au/terms-conditions/">
                    Terms & Conditions
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="https://curu.com.au/privacy-policy/">
                    Privacy policy
                  </Link>
                </li>
              </ul>
            </div>
            <div className="col-6 col-lg-4 app-download">
              <div className="h6">DOWNLOAD THE APP</div>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link
                    to="https://play.google.com/store/apps/details?id=com.curu"
                    className="btn btn-light text-nowrap fs-7"
                  >
                    <Icons.GooglePlay />
                    <span className="px-2">Get from Google Play store</span>
                  </Link>
                </li>
                <li className="mb-2">
                  <Link
                    to="https://apps.apple.com/us/app/curu-your-pocket-beauty-guru/id1581741630"
                    className="btn btn-light text-nowrap fs-7"
                  >
                    <Icons.Apple />
                    <span className="px-2">Get from Apple store</span>
                  </Link>
                </li>
              </ul>
            </div>
            <div className="col-6 col-lg">
              <div className="h6">FOLLOW US</div>
              <div className="d-flex">
                <Link to="https://www.instagram.com/curuclub/" className="me-2">
                  <Icons.Instagram />
                </Link>
                <Link
                  to="https://au.linkedin.com/company/curuclub"
                  className="me-2"
                >
                  <Icons.LinkedIn />
                </Link>
                <Link to="#" className="me-2">
                  <Icons.X />
                </Link>
                <Link to="#">
                  <Icons.TiTok />
                </Link>
              </div>
            </div>
            <div className="col-6 col-lg">
              <div className="h6">GET IN TOUCH</div>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link to="/feedback">Contact us</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
      <div
        className="modal fade"
        ref={searchModalInstance}
        tabIndex="-1"
        aria-labelledby="searchModalLabel"
        aria-hidden="true"
      ></div>
      <div
        className={`modal fade ${isModalOpen ? "show" : ""}`}
        style={{ display: isModalOpen ? "block" : "none" }}
        id="filterModal"
        tabIndex="-1"
        aria-labelledby="filterModalLabel"
        aria-hidden={!isModalOpen}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="filterModalLabel">
                Filter Products
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <GlobalFilter
                items={filterItems}
                onFilterApplied={handleCloseModal}
                onFilterChange={setIsFilterActive}
                accordionId="global-filter-accordion"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
